/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { Dispatch } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import { VisibilityState } from "../../model/entities";
import { AppVisibilityProvider } from "../../model/infrastructure";
import { Log, Sage } from "../../model/logging";
import type { UnityRootState } from "../UnityAppStore";
import type { AppVisibilityState } from "./appVisibility.types";
import { DeviceModel } from "../../model/device";
import { StorageAdapter } from "../../model/storage";
import { STORAGE_KEY_LANGUAGE } from "../../model/storage/storageConfig";
import { DEFAULT_LANGUAGE } from "../../config";

const TAG: string = "appVisibilitySlice";

const INITIAL_STATE: AppVisibilityState = {
  visibilityState: VisibilityState.FOREGROUND
};

const appVisibilitySlice = createSlice({
  name: "appVisibility",
  initialState: INITIAL_STATE,
  reducers: {
    appWentToBackground(state) {
      state.visibilityState = VisibilityState.BACKGROUND;
    },
    appCameToForeground(state) {
      state.visibilityState = VisibilityState.FOREGROUND;
    }
  }
});

const validateLanguageOnForeground = async (): Promise<void> => {
  try {
    const storage: StorageAdapter = StorageAdapter.getInstance();
    const isLanguageSupported: boolean =
      DeviceModel.getTVProperties().hasSupportedLanguage();
    const currentLanguage: string = isLanguageSupported
      ? DeviceModel.getTVProperties().getLang()
      : DEFAULT_LANGUAGE;
    const storedLanguage: string | null =
      await storage.get<string>(STORAGE_KEY_LANGUAGE);

    if (storedLanguage === currentLanguage) {
      return;
    }

    if (storedLanguage !== null) {
      Log.w(
        TAG,
        `Foreground language changed: "${storedLanguage}" → "${currentLanguage}" — restarting app`
      );
      DeviceModel.restartApplication();
    }

    await storage.set(
      STORAGE_KEY_LANGUAGE,
      currentLanguage,
      Number.POSITIVE_INFINITY
    );
  } catch (error: unknown) {
    Log.e(TAG, "validateLocaleOnForeground()", error);
  }
};

export const { appWentToBackground, appCameToForeground } =
  appVisibilitySlice.actions;

/**
 * Subscribes to the centralized AppVisibilityProvider and bridges
 * visibility changes into Redux. Returns an unsubscribe function
 * that the caller MUST invoke on cleanup (e.g. component unmount).
 */
export const initAppVisibilityListener =
  (): ((dispatch: Dispatch) => () => void) =>
  (dispatch: Dispatch): (() => void) => {
    const provider: AppVisibilityProvider = AppVisibilityProvider.getInstance();

    const callback = (isBackground: boolean): void => {
      if (isBackground) {
        Log.i(TAG, "initAppVisibilityListener() App went to background");
        dispatch(appWentToBackground());
      } else {
        Log.i(TAG, "initAppVisibilityListener() App came to foreground");
        Sage.flush();
        dispatch(appCameToForeground());
        validateLanguageOnForeground();
      }
    };

    provider.subscribe(callback);
    Log.i(TAG, "initAppVisibilityListener() Subscribed to provider");

    return (): void => {
      provider.unsubscribe(callback);
      Log.i(TAG, "initAppVisibilityListener() Unsubscribed from provider");
    };
  };

export const selectVisibilityState = (state: UnityRootState): VisibilityState =>
  state.appVisibility.visibilityState;

export const selectIsAppInBackground = (state: UnityRootState): boolean =>
  state.appVisibility.visibilityState === VisibilityState.BACKGROUND;

export const selectIsAppInForeground = (state: UnityRootState): boolean =>
  state.appVisibility.visibilityState === VisibilityState.FOREGROUND;

export default appVisibilitySlice.reducer;
